import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Upload, Send, FileText, Settings, Zap, Wrench, Book, Trash2, 
  Download, Code, Eye, Search, Save, FolderOpen, Copy, X,
  Terminal, Database, Network, Shield, Cpu, Activity, AlertCircle,
  CheckCircle, Play, RefreshCw, Grid, List, Plus, Bookmark,
  Clock, History, HelpCircle, ChevronDown, ChevronRight, Layers,
  Box, FileCode, BookOpen, MessageSquare, Edit2, Check,
  ArrowRight, ArrowLeft, Info, Filter, Star, Hash, Binary,
  Moon, Sun, Palette, Maximize2, Minimize2, Volume2, VolumeX,
  Share2, Link2, ExternalLink, Tag, MoreVertical, Menu,
  ChevronUp, Award, Sparkles, Lightbulb, Target, TrendingUp, Pause
} from 'lucide-react';

const PLCProgrammerAssistant = () => {
  // ==================== STATE MANAGEMENT ====================
  const [apiEndpoint, setApiEndpoint] = useState('https://anthropicapi-mtbat26jia-uc.a.run.app');
  
  // Document Management
  const [documents, setDocuments] = useState([]);
  const [activeDocId, setActiveDocId] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [documentIndex, setDocumentIndex] = useState({});
  const [showDocPreview, setShowDocPreview] = useState(false);
  const [documentContent, setDocumentContent] = useState({});
  
  // Chat & Messages
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState([
    "How do I implement a motor start/stop circuit?",
    "Explain the difference between TON and TOF timers",
    "What are the safety requirements for emergency stops?",
    "How to configure Modbus communication?"
  ]);
  
  // Ladder Logic Editor
  const [ladderRungs, setLadderRungs] = useState([]);
  const [selectedRung, setSelectedRung] = useState(null);
  const [ladderMode, setLadderMode] = useState('edit');
  const [simulationRunning, setSimulationRunning] = useState(false);
  
  // Code Templates
  const [codeTemplates, setCodeTemplates] = useState([
    {
      id: 1,
      name: 'Motor Start/Stop with Interlock',
      category: 'Motor Control',
      language: 'ladder',
      difficulty: 'Beginner',
      tags: ['motor', 'safety', 'interlock'],
      code: `|--[/]---[/]---[/]---( )--|
|  Stop  E-Stop Fault Motor |
|                           |
|--[ ]----[ ]---| |---( )--|
| Start  Ready  M1    M1   |
|                           |
|--[ ]---[TON]---( )-------|
|  M1    T1:3s   Aux       |`,
      description: 'Complete motor control with safety interlocks, fault detection, and auxiliary contact',
      usage: 156,
      rating: 4.8
    },
    {
      id: 2,
      name: 'Cascade Timer Sequence',
      category: 'Timers',
      language: 'ladder',
      difficulty: 'Intermediate',
      tags: ['timer', 'sequence', 'cascade'],
      code: `|--[ ]---[TON]---( )--|
| Start  T1:5s   Step1|
|                     |
|--[ ]---[TON]---( )--|
| Step1  T2:3s   Step2|
|                     |
|--[ ]---[TON]---( )--|
| Step2  T3:7s   Step3|`,
      description: 'Sequential timer cascade for multi-step processes',
      usage: 89,
      rating: 4.6
    }
  ]);
  
  // UI State
  const [activeTab, setActiveTab] = useState('chat');
  const [showSettings, setShowSettings] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [codeLanguage, setCodeLanguage] = useState('ladder');
  const [darkMode, setDarkMode] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [templateFilter, setTemplateFilter] = useState('all');
  const [showTemplateDetails, setShowTemplateDetails] = useState(null);
  const [conversionSource, setConversionSource] = useState('');
  const [conversionTarget, setConversionTarget] = useState('');
  const [conversionSourceLang, setConversionSourceLang] = useState('ladder');
  const [conversionTargetLang, setConversionTargetLang] = useState('structured_text');
  
  // Statistics
  const [stats, setStats] = useState({
    totalQuestions: 0,
    documentsProcessed: 0,
    templatesUsed: 0,
    bookmarksSaved: 0
  });
  
  // Refs
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const chatInputRef = useRef(null);

  // ==================== INITIALIZATION ====================
  useEffect(() => {
    // Load PDF.js
    const loadPdfJs = async () => {
      if (!window.pdfjsLib) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        script.onload = () => {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        };
        document.head.appendChild(script);
      }
    };
    loadPdfJs();

    // Load saved preferences
    const savedDarkMode = localStorage.getItem('plc-darkmode');
    if (savedDarkMode === 'true') setDarkMode(true);

    // Load stats
    const savedStats = localStorage.getItem('plc-stats');
    if (savedStats) setStats(JSON.parse(savedStats));
  }, []);

  // Save stats
  useEffect(() => {
    localStorage.setItem('plc-stats', JSON.stringify(stats));
  }, [stats]);

  // Dark mode toggle
  useEffect(() => {
    localStorage.setItem('plc-darkmode', darkMode);
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ==================== ENHANCED SYSTEM PROMPT ====================
  const getSystemPrompt = () => {
    let documentContext = '';
    
    if (documents.length > 0) {
      documentContext = `
You have access to the following uploaded documents:

${documents.map((doc, idx) => `
Document ${idx + 1}: "${doc.name}"
Content:
${doc.content}
---
`).join('\n')}

IMPORTANT: Use the information from these documents to answer questions when relevant. If the user asks about something covered in the documents, reference the specific document and provide accurate information from it.
`;
    }

    return `You are an expert PLC (Programmable Logic Controller) programming assistant with deep knowledge of industrial automation, safety systems, and all major PLC platforms.

Your expertise includes:
- IEC 61131-3 programming languages (Ladder Logic, Structured Text, Function Block, Instruction List, Sequential Function Chart)
- Major PLC brands (Siemens, Allen-Bradley, Schneider, ABB, Omron, Mitsubishi, Beckhoff)
- Safety systems (SIL ratings, Performance Levels, E-stops, interlocks)
- Industrial communication protocols (Modbus, Profibus, Profinet, EtherNet/IP, etc.)
- Motor control, process control, motion control
- HMI/SCADA integration
- Troubleshooting and diagnostics

${documentContext}

Always provide:
1. Clear, practical solutions that work in real industrial environments
2. Safety considerations for any implementation
3. Code examples when relevant
4. Best practices and standards compliance
5. Troubleshooting tips

When providing code, use appropriate formatting and explain the logic clearly.`;
  };

  // ==================== PDF PROCESSING ====================
  const extractTextFromPDF = async (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      
      fileReader.onload = async function(event) {
        try {
          // Wait for PDF.js to load
          let attempts = 0;
          while (!window.pdfjsLib && attempts < 50) {
            await new Promise(r => setTimeout(r, 100));
            attempts++;
          }

          if (!window.pdfjsLib) {
            throw new Error('PDF.js library not loaded. Please refresh the page.');
          }

          const arrayBuffer = event.target.result;
          const loadingTask = window.pdfjsLib.getDocument({
            data: new Uint8Array(arrayBuffer),
            cMapUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/',
            cMapPacked: true,
            standardFontDataUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/standard_fonts/'
          });
          
          const pdf = await loadingTask.promise;
          let fullText = '';
          const pages = [];
          
          for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            
            // Improved text extraction
            let pageText = '';
            let lastY = null;
            
            textContent.items.forEach(item => {
              // Add line break if Y position changes significantly
              if (lastY !== null && Math.abs(item.transform[5] - lastY) > 5) {
                pageText += '\n';
              }
              pageText += item.str + ' ';
              lastY = item.transform[5];
            });
            
            pageText = pageText.trim();
            
            pages.push({
              pageNumber: pageNum,
              text: pageText
            });
            
            fullText += `\n[Page ${pageNum}]\n${pageText}\n`;
          }
          
          resolve({ 
            text: fullText, 
            pages,
            success: true 
          });
          
        } catch (error) {
          console.error('PDF extraction error:', error);
          reject(error);
        }
      };
      
      fileReader.onerror = () => reject(new Error('Failed to read file'));
      fileReader.readAsArrayBuffer(file);
    });
  };

  // ==================== DOCUMENT MANAGEMENT ====================
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;
    
    setIsLoading(true);
    let successCount = 0;
    let errorMessages = [];
    
    for (const file of files) {
      if (file.type !== 'application/pdf') {
        errorMessages.push(`${file.name}: Only PDF files are supported`);
        continue;
      }

      try {
        const { text, pages, success } = await extractTextFromPDF(file);
        
        if (!success || !text || text.trim().length === 0) {
          errorMessages.push(`${file.name}: No text content found`);
          continue;
        }

        const docId = Date.now() + Math.random();
        
        // Create technical index
        const index = createTechnicalIndex(text);
        
        const newDoc = {
          id: docId,
          name: file.name,
          type: 'PDF',
          content: text,
          pages: pages,
          index: index,
          uploadTime: new Date().toISOString(),
          size: file.size,
          wordCount: text.split(/\s+/).length
        };
        
        setDocuments(prev => [...prev, newDoc]);
        setDocumentContent(prev => ({
          ...prev,
          [docId]: text
        }));
        setDocumentIndex(prev => ({...prev, [docId]: index}));
        
        if (!activeDocId) {
          setActiveDocId(docId);
        }
        
        successCount++;
        
      } catch (error) {
        console.error(`Error processing ${file.name}:`, error);
        errorMessages.push(`${file.name}: ${error.message}`);
      }
    }
    
    // Update stats
    setStats(prev => ({
      ...prev,
      documentsProcessed: prev.documentsProcessed + successCount
    }));
    
    // Show result messages
    if (successCount > 0) {
      const successMsg = {
        role: 'system',
        content: `✅ Successfully loaded ${successCount} document${successCount > 1 ? 's' : ''}! The AI now has access to this information.`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, successMsg]);
    }
    
    if (errorMessages.length > 0) {
      const errorMsg = {
        role: 'system',
        content: `⚠️ Errors:\n${errorMessages.join('\n')}`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMsg]);
    }
    
    setIsLoading(false);
    event.target.value = '';
  };

  const createTechnicalIndex = (text) => {
    const technicalTerms = {
      plc: [],
      io: [],
      safety: [],
      communication: [],
      motors: [],
      sensors: [],
      instructions: []
    };
    
    // Extract PLC-related terms
    const plcTerms = text.match(/\b(S7-\d+|ControlLogix|CompactLogix|Modicon|TIA Portal|Step 7|Studio 5000|RSLogix|Siemens|Allen-Bradley|Schneider|ABB|Omron|Mitsubishi)\b/gi);
    if (plcTerms) technicalTerms.plc = [...new Set(plcTerms.map(t => t.toLowerCase()))];
    
    // Extract I/O addresses
    const ioTerms = text.match(/\b([IQM]\d+\.\d+|%[IQM]\d+\.\d+|DB\d+|T\d+|C\d+|[IQ]:\d+\/\d+)\b/g);
    if (ioTerms) technicalTerms.io = [...new Set(ioTerms)];
    
    // Extract safety terms
    const safetyTerms = text.match(/\b(emergency stop|e-stop|safety|SIL \d|PLd|PLe|Category [1-4]|light curtain|safety relay|interlock|guard|dual channel)\b/gi);
    if (safetyTerms) technicalTerms.safety = [...new Set(safetyTerms.map(t => t.toLowerCase()))];
    
    // Extract communication protocols
    const commTerms = text.match(/\b(Modbus|Profibus|Profinet|EtherNet\/IP|DeviceNet|CANopen|OPC UA|MQTT|TCP\/IP|RS-?485|RS-?232|Ethernet|Serial)\b/gi);
    if (commTerms) technicalTerms.communication = [...new Set(commTerms.map(t => t.toLowerCase()))];
    
    // Extract motor-related terms
    const motorTerms = text.match(/\b(motor|VFD|variable frequency drive|servo|stepper|encoder|inverter|soft start|DOL|star-delta)\b/gi);
    if (motorTerms) technicalTerms.motors = [...new Set(motorTerms.map(t => t.toLowerCase()))];
    
    // Extract sensor terms
    const sensorTerms = text.match(/\b(sensor|proximity|photoelectric|ultrasonic|temperature|pressure|flow|level|limit switch|encoder|thermocouple|RTD|PT100)\b/gi);
    if (sensorTerms) technicalTerms.sensors = [...new Set(sensorTerms.map(t => t.toLowerCase()))];
    
    // Extract instruction terms
    const instructionTerms = text.match(/\b(TON|TOF|CTU|CTD|MOV|ADD|SUB|MUL|DIV|AND|OR|NOT|XOR|SET|RESET|CALL|JMP|RET|MCR)\b/g);
    if (instructionTerms) technicalTerms.instructions = [...new Set(instructionTerms)];
    
    return technicalTerms;
  };

  const searchDocuments = useCallback((query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    const results = [];
    const searchTerm = query.toLowerCase();
    
    documents.forEach(doc => {
      doc.pages.forEach(page => {
        if (page.text.toLowerCase().includes(searchTerm)) {
          const index = page.text.toLowerCase().indexOf(searchTerm);
          const contextStart = Math.max(0, index - 100);
          const contextEnd = Math.min(page.text.length, index + searchTerm.length + 100);
          const context = page.text.substring(contextStart, contextEnd);
          
          results.push({
            docId: doc.id,
            docName: doc.name,
            pageNumber: page.pageNumber,
            context: context,
            highlight: searchTerm
          });
        }
      });
    });
    
    setSearchResults(results.slice(0, 10));
  }, [documents]);

  // ==================== CHAT FUNCTIONS ====================
  const callAPI = async (userMessage) => {
    const systemPrompt = getSystemPrompt();
    
    try {
      // Prepare message history for context
      const messageHistory = messages
        .filter(m => m.role !== 'system')
        .slice(-10) // Keep last 10 messages for context
        .map(m => ({
          role: m.role === 'user' ? 'user' : 'assistant',
          content: m.content
        }));

      const requestBody = {
        model: "claude-sonnet-4-20250514",
        max_tokens: 18000,
        temperature: 0.4,
        system: systemPrompt,
        messages: [
          ...messageHistory,
          { role: 'user', content: userMessage }
        ]
      };

      console.log('Sending request to API...');
      
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', response.status, errorText);
        throw new Error(`API Error (${response.status}): ${errorText || 'Unknown error'}`);
      }

      const data = await response.json();
      
      if (!data.content || !data.content[0] || !data.content[0].text) {
        throw new Error('Invalid response format from API');
      }
      
      return data.content[0].text;
      
    } catch (error) {
      console.error('API call error:', error);
      throw error;
    }
  };

  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    if (!currentMessage.trim() || isLoading) return;

    const userMessage = currentMessage.trim();
    setCurrentMessage('');
    
    // Add user message
    const userMsg = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMsg]);
    
    setIsLoading(true);
    setIsTyping(true);

    // Update stats
    setStats(prev => ({ ...prev, totalQuestions: prev.totalQuestions + 1 }));

    try {
      // Add context about loaded documents to the message if relevant
      let enhancedMessage = userMessage;
      if (documents.length > 0) {
        const docList = documents.map(d => d.name).join(', ');
        enhancedMessage = `${userMessage}\n\n[Context: I have uploaded ${documents.length} document(s): ${docList}. Please use information from these documents if relevant to answer my question.]`;
      }

      const response = await callAPI(enhancedMessage);
      
      setIsTyping(false);
      
      // Add assistant response
      const assistantMsg = {
        role: 'assistant',
        content: response,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMsg]);
      
      // Add to chat history
      setChatHistory(prev => [...prev, {
        id: Date.now(),
        question: userMessage,
        answer: response,
        timestamp: new Date().toISOString()
      }]);
      
    } catch (error) {
      console.error('Message handling error:', error);
      setIsTyping(false);
      
      const errorMsg = {
        role: 'assistant',
        content: `⚠️ Error: ${error.message}\n\nTroubleshooting:\n1. Check your internet connection\n2. Verify the API endpoint is correct\n3. Try again in a moment`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMsg]);
    }
    
    setIsLoading(false);
  };

  const exportChatHistory = () => {
    const exportData = {
      exportDate: new Date().toISOString(),
      messages: messages,
      documents: documents.map(d => ({ 
        name: d.name, 
        pages: d.pages.length,
        uploadTime: d.uploadTime,
        wordCount: d.wordCount
      })),
      bookmarks: bookmarks,
      stats: stats
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `plc-assistant-export-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ==================== LADDER LOGIC EDITOR ====================
  const ladderElements = [
    { type: 'contact_no', symbol: '─[ ]─', name: 'NO Contact', icon: '[ ]' },
    { type: 'contact_nc', symbol: '─[/]─', name: 'NC Contact', icon: '[/]' },
    { type: 'coil', symbol: '─( )─', name: 'Output Coil', icon: '( )' },
    { type: 'timer_on', symbol: '─[TON]─', name: 'Timer ON', icon: 'TON' },
    { type: 'timer_off', symbol: '─[TOF]─', name: 'Timer OFF', icon: 'TOF' },
    { type: 'counter_up', symbol: '─[CTU]─', name: 'Counter Up', icon: 'CTU' },
    { type: 'counter_down', symbol: '─[CTD]─', name: 'Counter Down', icon: 'CTD' }
  ];

  const addLadderRung = () => {
    const newRung = {
      id: Date.now(),
      elements: [],
      comment: '',
      address: `Rung ${ladderRungs.length}`,
      isValid: true
    };
    setLadderRungs(prev => [...prev, newRung]);
    setSelectedRung(newRung.id);
  };

  const addElementToRung = (rungId, element) => {
    setLadderRungs(prev => prev.map(rung => {
      if (rung.id === rungId) {
        return {
          ...rung,
          elements: [...rung.elements, { ...element, id: Date.now(), tag: '' }]
        };
      }
      return rung;
    }));
  };

  // ==================== CODE CONVERTER ====================
  const convertCode = () => {
    const conversions = {
      'ladder_to_structured_text': (code) => {
        let st = '(* Converted from Ladder Logic *)\n\n';
        const lines = code.split('\n');
        
        lines.forEach(line => {
          if (line.includes('[ ]') && line.includes('( )')) {
            st += `IF input THEN\n    output := TRUE;\nEND_IF;\n\n`;
          } else if (line.includes('[/]')) {
            st += `IF NOT input THEN\n    (* Action *)\nEND_IF;\n\n`;
          }
        });
        
        return st || '(* Add your ladder logic to convert *)';
      },
      'structured_text_to_ladder': (code) => {
        let ladder = '// Converted from Structured Text\n\n';
        
        if (code.includes('IF') && code.includes('THEN')) {
          ladder += '|--[ ]---( )--|\n';
        }
        if (code.includes('NOT')) {
          ladder += '|--[/]---( )--|\n';
        }
        
        return ladder || '// Add your structured text to convert';
      }
    };
    
    const conversionKey = `${conversionSourceLang}_to_${conversionTargetLang}`;
    if (conversions[conversionKey]) {
      setConversionTarget(conversions[conversionKey](conversionSource));
    } else {
      setConversionTarget(`// Conversion from ${conversionSourceLang} to ${conversionTargetLang} not yet implemented`);
    }
  };

  // ==================== UI HELPERS ====================
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const highlightCode = (code, language) => {
    // Simple syntax highlighting
    let highlighted = code;
    highlighted = highlighted.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    
    // Keywords
    const keywords = ['IF', 'THEN', 'ELSE', 'END_IF', 'FOR', 'TO', 'DO', 'END_FOR', 'VAR', 'END_VAR'];
    keywords.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'g');
      highlighted = highlighted.replace(regex, `<span style="color: #3b82f6; font-weight: bold;">${keyword}</span>`);
    });
    
    // Comments
    highlighted = highlighted.replace(/(\/\/.*$)/gm, '<span style="color: #10b981; font-style: italic;">$1</span>');
    highlighted = highlighted.replace(/(\(\*[\s\S]*?\*\))/g, '<span style="color: #10b981; font-style: italic;">$1</span>');
    
    return highlighted;
  };

  // ==================== RENDER ====================
  return (
    <div className={`min-h-screen ${darkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-gray-50 to-blue-50'} flex flex-col transition-all duration-300`}>
      {/* Header */}
      <div className={`${darkMode ? 'bg-gradient-to-r from-gray-800 to-gray-900' : 'bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700'} text-white shadow-xl relative overflow-hidden`}>
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px)'
        }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-4 md:py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-white/20 backdrop-blur-sm p-2.5 rounded-xl shadow-lg">
                <Cpu className="w-7 h-7 md:w-8 md:h-8" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
                  PLC Assistant
                  <span className="inline-flex items-center gap-1 text-xs bg-gradient-to-r from-yellow-400 to-amber-400 text-gray-900 px-2 py-1 rounded-full font-bold shadow-lg">
                    <Sparkles className="w-3 h-3" />
                    PRO
                  </span>
                </h1>
                <p className="text-xs md:text-sm text-blue-100 hidden md:block">
                  Industrial Automation Expert • PDF Analysis • Code Converter
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 md:space-x-3">
              <button
                onClick={exportChatHistory}
                className="p-2 hover:bg-white/20 rounded-lg transition-all"
                title="Export History"
              >
                <Download className="w-5 h-5" />
              </button>
              
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 hover:bg-white/20 rounded-lg transition-all"
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 hover:bg-white/20 rounded-lg transition-all"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Stats Bar */}
          <div className="hidden md:flex items-center space-x-6 mt-3 text-xs text-blue-100">
            <div className="flex items-center gap-1">
              <MessageSquare className="w-3 h-3" />
              {stats.totalQuestions} Questions
            </div>
            <div className="flex items-center gap-1">
              <FileText className="w-3 h-3" />
              {stats.documentsProcessed} Documents
            </div>
            <div className="flex items-center gap-1">
              <Code className="w-3 h-3" />
              {stats.templatesUsed} Templates Used
            </div>
            <div className="flex items-center gap-1">
              <Bookmark className="w-3 h-3" />
              {stats.bookmarksSaved} Bookmarks
            </div>
          </div>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'} border-b shadow-lg transition-all duration-300`}>
          <div className="max-w-7xl mx-auto p-4">
            <h3 className={`font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'} mb-4 flex items-center gap-2`}>
              <Settings className="w-4 h-4" /> Configuration
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                  API Endpoint
                </label>
                <input
                  type="text"
                  value={apiEndpoint}
                  onChange={(e) => setApiEndpoint(e.target.value)}
                  className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                  Default Code Language
                </label>
                <select
                  value={codeLanguage}
                  onChange={(e) => setCodeLanguage(e.target.value)}
                  className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="ladder">Ladder Logic</option>
                  <option value="structured_text">Structured Text</option>
                  <option value="function_block">Function Block</option>
                  <option value="instruction_list">Instruction List</option>
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                  Theme
                </label>
                <select
                  value={darkMode ? 'dark' : 'light'}
                  onChange={(e) => setDarkMode(e.target.value === 'dark')}
                  className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Management Bar */}
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'} border-b shadow-sm transition-all`}>
        <div className="max-w-7xl mx-auto p-3">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
            <div className="flex items-center space-x-3 w-full md:w-auto">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                multiple
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all shadow-md hover:shadow-lg ${
                  darkMode 
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white' 
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Upload className="w-4 h-4" />
                <span>{isLoading ? 'Processing...' : 'Upload PDFs'}</span>
              </button>
              
              {documents.length > 0 && (
                <div className={`flex items-center space-x-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <FileText className="w-4 h-4" />
                  <span className="font-medium">{documents.length}</span>
                  <span>document{documents.length !== 1 && 's'} loaded</span>
                </div>
              )}
            </div>
            
            {documents.length > 0 && (
              <div className="flex items-center space-x-2 w-full md:w-auto">
                <div className="relative flex-1 md:flex-initial">
                  <Search className={`w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 ${
                    darkMode ? 'text-gray-500' : 'text-gray-400'
                  }`} />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search documents..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      searchDocuments(e.target.value);
                    }}
                    className={`pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 w-full md:w-72 ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' 
                        : 'bg-white border-gray-300 placeholder-gray-400'
                    }`}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSearchResults([]);
                      }}
                      className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${
                        darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                      }`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Document Tabs */}
          {documents.length > 0 && (
            <div className="flex items-center space-x-2 mt-3 pb-2 overflow-x-auto">
              {documents.map(doc => (
                <button
                  key={doc.id}
                  onClick={() => setActiveDocId(doc.id)}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg whitespace-nowrap transition-all group ${
                    activeDocId === doc.id 
                      ? darkMode 
                        ? 'bg-blue-900/50 text-blue-300 border border-blue-600' 
                        : 'bg-blue-100 text-blue-700 border border-blue-300'
                      : darkMode
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Book className="w-3 h-3" />
                  <span className="text-sm font-medium">{doc.name.substring(0, 30)}{doc.name.length > 30 && '...'}</span>
                  <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {doc.pages.length}p
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDocuments(prev => prev.filter(d => d.id !== doc.id));
                      setDocumentContent(prev => {
                        const newContent = {...prev};
                        delete newContent[doc.id];
                        return newContent;
                      });
                      if (activeDocId === doc.id) {
                        setActiveDocId(documents[0]?.id || null);
                      }
                    }}
                    className={`ml-1 opacity-0 group-hover:opacity-100 transition-all ${
                      darkMode ? 'hover:text-red-400' : 'hover:text-red-600'
                    }`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Tabs */}
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'} border-b shadow-sm sticky top-0 z-40`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex overflow-x-auto">
            {[
              { id: 'chat', icon: MessageSquare, label: 'AI Chat' },
              { id: 'ladder', icon: Grid, label: 'Ladder Editor' },
              { id: 'templates', icon: FileCode, label: 'Templates' },
              { id: 'converter', icon: ArrowRight, label: 'Converter' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-3 border-b-3 transition-all whitespace-nowrap ${
                  activeTab === tab.id 
                    ? `border-blue-500 text-blue-600 ${darkMode ? 'bg-gray-700/50' : 'bg-blue-50/50'}`
                    : `border-transparent ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'} hover:bg-gray-50/50`
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="font-medium text-sm md:text-base">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 max-w-7xl mx-auto w-full p-2 md:p-4">
        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          } h-full flex flex-col transition-all`}>
            <div className="flex-1 p-4 md:p-6 overflow-y-auto">
              {messages.length === 0 && (
                <div className="text-center py-8 md:py-12">
                  <div className="relative inline-block">
                    <div className="absolute inset-0 animate-pulse bg-blue-400 rounded-full blur-xl opacity-20"></div>
                    <Zap className={`w-16 h-16 md:w-20 md:h-20 mx-auto mb-6 ${
                      darkMode ? 'text-blue-400' : 'text-blue-500'
                    }`} />
                  </div>
                  
                  <h3 className={`text-xl md:text-2xl font-bold mb-4 ${
                    darkMode ? 'text-gray-200' : 'text-gray-800'
                  }`}>
                    PLC Programming Assistant Ready
                  </h3>
                  
                  <p className={`text-sm md:text-base mb-8 ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {documents.length > 0 
                      ? `📚 ${documents.length} document${documents.length > 1 ? 's' : ''} loaded - I can answer questions about your PDFs!`
                      : 'Upload PDFs or ask PLC programming questions'
                    }
                  </p>
                  
                  {/* Suggested Questions */}
                  <div className="space-y-2">
                    <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-3`}>
                      Quick Questions:
                    </p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {suggestedQuestions.map((question, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentMessage(question)}
                          className={`px-3 py-2 rounded-lg text-sm transition-all ${
                            darkMode 
                              ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Messages */}
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} mb-4`}
                >
                  <div
                    className={`max-w-full md:max-w-4xl p-4 rounded-xl shadow-md ${
                      message.role === 'user'
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                        : message.role === 'system'
                        ? darkMode 
                          ? 'bg-green-900/50 text-green-300 border border-green-700'
                          : 'bg-green-50 text-green-800 border border-green-200'
                        : message.isError
                        ? darkMode
                          ? 'bg-red-900/50 text-red-300 border border-red-700'
                          : 'bg-red-50 text-red-800 border border-red-200'
                        : darkMode
                        ? 'bg-gray-700 text-gray-200 border border-gray-600'
                        : 'bg-gray-50 text-gray-800 border border-gray-200'
                    }`}
                  >
                    <div className="whitespace-pre-wrap break-words">{message.content}</div>
                    <div className={`text-xs mt-2 ${
                      message.role === 'user' ? 'text-blue-200' : darkMode ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Typing Indicator */}
              {isTyping && (
                <div className="flex justify-start mb-4">
                  <div className={`p-4 rounded-xl ${
                    darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gray-100 border border-gray-200'
                  }`}>
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        AI is thinking...
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className={`border-t p-4 ${darkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'}`}>
              <form onSubmit={handleSendMessage} className="flex space-x-3">
                <input
                  ref={chatInputRef}
                  type="text"
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  placeholder={
                    documents.length > 0
                      ? "Ask about your PDFs or any PLC programming question..."
                      : "Ask any PLC programming question..."
                  }
                  disabled={isLoading}
                  className={`flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-all ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' 
                      : 'bg-white border-gray-300 placeholder-gray-400'
                  } ${isLoading ? 'opacity-50' : ''}`}
                />
                <button
                  type="submit"
                  disabled={!currentMessage.trim() || isLoading}
                  className={`px-6 py-3 rounded-lg flex items-center space-x-2 transition-all shadow-md hover:shadow-lg ${
                    !currentMessage.trim() || isLoading
                      ? 'bg-gray-400 cursor-not-allowed opacity-50'
                      : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                  }`}
                >
                  <Send className="w-4 h-4" />
                  <span className="hidden md:inline">Send</span>
                </button>
              </form>
              
              {messages.length > 0 && (
                <div className="flex items-center justify-between mt-3">
                  <button
                    onClick={() => setMessages([])}
                    className={`text-sm flex items-center space-x-1 ${
                      darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Clear Chat</span>
                  </button>
                  <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {messages.length} messages • {documents.length} documents
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Ladder Editor Tab */}
        {activeTab === 'ladder' && (
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          } p-4 md:p-6 transition-all`}>
            <div className="mb-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <h2 className={`text-xl font-bold flex items-center gap-2 ${
                darkMode ? 'text-gray-200' : 'text-gray-800'
              }`}>
                <Grid className="w-5 h-5" /> Ladder Logic Editor
              </h2>
              <button
                onClick={addLadderRung}
                className="flex items-center space-x-2 px-4 py-1.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 shadow-md text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Add Rung</span>
              </button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Elements Palette */}
              <div className="lg:col-span-1">
                <h3 className={`font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Elements
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {ladderElements.map(elem => (
                    <button
                      key={elem.type}
                      onClick={() => selectedRung && addElementToRung(selectedRung, elem)}
                      disabled={!selectedRung}
                      className={`p-3 border rounded-lg transition-all ${
                        !selectedRung 
                          ? 'opacity-50 cursor-not-allowed'
                          : darkMode
                            ? 'bg-gray-700 border-gray-600 hover:bg-gray-600 text-gray-200'
                            : 'bg-white border-gray-300 hover:bg-blue-50'
                      }`}
                    >
                      <div className="font-mono text-lg font-bold text-blue-500">{elem.icon}</div>
                      <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {elem.name}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Ladder Diagram */}
              <div className="lg:col-span-2">
                <h3 className={`font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Ladder Diagram
                </h3>
                <div className={`border rounded-lg p-4 min-h-[400px] ${
                  darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-300'
                }`}>
                  {ladderRungs.length === 0 ? (
                    <div className={`text-center py-16 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      <Grid className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      Click "Add Rung" to start
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {ladderRungs.map((rung, idx) => (
                        <div 
                          key={rung.id}
                          onClick={() => setSelectedRung(rung.id)}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            selectedRung === rung.id 
                              ? 'border-blue-500'
                              : darkMode
                                ? 'border-gray-600 hover:border-gray-500'
                                : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-sm font-bold ${
                              darkMode ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Rung {idx}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setLadderRungs(prev => prev.filter(r => r.id !== rung.id));
                                if (selectedRung === rung.id) setSelectedRung(null);
                              }}
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          
                          <div className={`font-mono text-sm ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                            <div className="flex items-center">
                              <span>├─</span>
                              {rung.elements.length === 0 ? (
                                <span className={darkMode ? 'text-gray-600' : 'text-gray-400'}>
                                  (Click elements to add)
                                </span>
                              ) : (
                                rung.elements.map((elem, i) => (
                                  <span key={elem.id}>
                                    {elem.symbol}
                                    {i < rung.elements.length - 1 && '─'}
                                  </span>
                                ))
                              )}
                              <span>─┤</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          } p-4 md:p-6 transition-all`}>
            <h2 className={`text-xl font-bold flex items-center gap-2 mb-6 ${
              darkMode ? 'text-gray-200' : 'text-gray-800'
            }`}>
              <FileCode className="w-5 h-5" /> Code Templates
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {codeTemplates.map(template => (
                <div 
                  key={template.id} 
                  className={`border rounded-xl p-5 hover:shadow-xl transition-all ${
                    darkMode 
                      ? 'bg-gray-750 border-gray-700 hover:border-blue-600' 
                      : 'bg-white border-gray-200 hover:border-blue-400'
                  }`}
                >
                  <h3 className={`font-bold text-lg mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    {template.name}
                  </h3>
                  
                  <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {template.description}
                  </p>
                  
                  <div className={`text-xs font-mono p-3 rounded-lg overflow-x-auto mb-3 ${
                    darkMode ? 'bg-gray-900' : 'bg-gray-50'
                  }`}>
                    <code className="whitespace-pre">
                      {template.code.substring(0, 150)}...
                    </code>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {template.category}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        template.difficulty === 'Beginner' 
                          ? darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-700'
                          : darkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {template.difficulty}
                      </span>
                    </div>
                    
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(template.code);
                        setStats(prev => ({ ...prev, templatesUsed: prev.templatesUsed + 1 }));
                      }}
                      className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg text-xs transition-all ${
                        darkMode 
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      <Copy className="w-3 h-3" />
                      <span>Copy</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Code Converter Tab */}
        {activeTab === 'converter' && (
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          } p-4 md:p-6 transition-all`}>
            <h2 className={`text-xl font-bold flex items-center gap-2 mb-6 ${
              darkMode ? 'text-gray-200' : 'text-gray-800'
            }`}>
              <ArrowRight className="w-5 h-5" /> Code Converter
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Source Language
                  </label>
                  <select 
                    value={conversionSourceLang}
                    onChange={(e) => setConversionSourceLang(e.target.value)}
                    className={`px-3 py-1.5 border rounded-lg text-sm ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200' 
                        : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="ladder">Ladder Logic</option>
                    <option value="structured_text">Structured Text</option>
                  </select>
                </div>
                <textarea
                  value={conversionSource}
                  onChange={(e) => setConversionSource(e.target.value)}
                  placeholder="Paste your source code here..."
                  className={`w-full h-80 p-4 border rounded-lg font-mono text-sm ${
                    darkMode 
                      ? 'bg-gray-900 border-gray-700 text-gray-200 placeholder-gray-600' 
                      : 'bg-gray-50 border-gray-300 placeholder-gray-400'
                  }`}
                />
              </div>
              
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Target Language
                  </label>
                  <select 
                    value={conversionTargetLang}
                    onChange={(e) => setConversionTargetLang(e.target.value)}
                    className={`px-3 py-1.5 border rounded-lg text-sm ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200' 
                        : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="structured_text">Structured Text</option>
                    <option value="ladder">Ladder Logic</option>
                  </select>
                </div>
                <div className={`w-full h-80 p-4 border rounded-lg font-mono text-sm overflow-auto ${
                  darkMode 
                    ? 'bg-gray-900 border-gray-700 text-gray-200' 
                    : 'bg-gray-50 border-gray-300'
                }`}>
                  {conversionTarget ? (
                    <pre dangerouslySetInnerHTML={{ __html: highlightCode(conversionTarget, conversionTargetLang) }} />
                  ) : (
                    <div className={darkMode ? 'text-gray-600' : 'text-gray-400'}>
                      Converted code will appear here...
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex items-center justify-center space-x-3">
              <button 
                onClick={convertCode}
                className="flex items-center space-x-2 px-6 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg shadow-md transition-all"
              >
                <ArrowRight className="w-4 h-4" />
                <span>Convert</span>
              </button>
              {conversionTarget && (
                <button
                  onClick={() => navigator.clipboard.writeText(conversionTarget)}
                  className={`flex items-center space-x-2 px-6 py-2.5 rounded-lg shadow-md transition-all ${
                    darkMode 
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  <Copy className="w-4 h-4" />
                  <span>Copy</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Search Results Overlay */}
      {searchResults.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden`}>
            <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
              <h3 className={`font-bold text-lg flex items-center gap-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                <Search className="w-5 h-5" />
                Search Results ({searchResults.length})
              </h3>
              <button
                onClick={() => {
                  setSearchResults([]);
                  setSearchQuery('');
                }}
                className={`p-2 rounded-lg transition-all ${
                  darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="overflow-y-auto max-h-[60vh] p-4">
              {searchResults.map((result, idx) => (
                <div 
                  key={idx}
                  onClick={() => {
                    setActiveDocId(result.docId);
                    setSearchResults([]);
                    setSearchQuery('');
                  }}
                  className={`p-4 rounded-lg mb-3 cursor-pointer transition-all ${
                    darkMode 
                      ? 'bg-gray-700 hover:bg-gray-600 border border-gray-600' 
                      : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className={`font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        {result.docName}
                      </h4>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Page {result.pageNumber}
                      </p>
                    </div>
                    <ExternalLink className={`w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    ...{result.context}...
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-t mt-auto`}>
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex flex-col md:flex-row items-center justify-between text-xs">
            <div className={`${darkMode ? 'text-gray-500' : 'text-gray-400'} flex items-center gap-2`}>
              <Shield className="w-3 h-3" />
              <span>Professional PLC Programming Assistant</span>
            </div>
            <div className={`${darkMode ? 'text-gray-600' : 'text-gray-400'} mt-2 md:mt-0`}>
              Powered by Claude AI
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PLCProgrammerAssistant;